/**
 * Global low-balance warning bar. Renders below the top bar on every
 * page whenever `balance_cents < low_balance_threshold_cents`.
 *
 * Dismissal is sessionStorage-scoped — if the user clicks X it stays
 * hidden for this browser tab session, but a fresh session or a new
 * tab surfaces it again. We deliberately don't use localStorage: a
 * persistent dismiss would let the user mute the warning forever even
 * as their balance keeps falling; every fresh session should re-remind.
 *
 * The dismiss is ALSO cleared automatically when the balance rises
 * above threshold (a top-up, admin adjustment) — stored state tracks
 * the balance it was dismissed at, so a later dip to a NEW low below
 * threshold re-surfaces the banner without requiring a tab close.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useBalance } from '@/providers/BalanceProvider';
import { useTheme } from '@/providers/ThemeProvider';

const DISMISS_KEY = 'meridian.lowBalanceDismissedAtCents';

function readDismissedCents(): number | null {
  try {
    const raw = sessionStorage.getItem(DISMISS_KEY);
    if (raw === null) return null;
    const n = Number.parseInt(raw, 10);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

function writeDismissedCents(cents: number): void {
  try {
    sessionStorage.setItem(DISMISS_KEY, String(cents));
  } catch {
    /* noop */
  }
}

function clearDismissed(): void {
  try {
    sessionStorage.removeItem(DISMISS_KEY);
  } catch {
    /* noop */
  }
}

export default function LowBalanceBanner() {
  const { isDark } = useTheme();
  const { balance, isLow } = useBalance();
  const navigate = useNavigate();
  // Bumped on dismiss so `useMemo` re-reads sessionStorage.
  const [dismissNonce, setDismissNonce] = useState(0);

  const shouldShow = useMemo(() => {
    if (!balance || !isLow) {
      // Balance is healthy → clear any prior dismiss marker so the next
      // dip starts from a clean slate.
      if (balance && !isLow) clearDismissed();
      return false;
    }
    const dismissedAt = readDismissedCents();
    if (dismissedAt === null) return true;
    // Re-show if balance dropped further since the last dismiss.
    return balance.balanceCents < dismissedAt;
    // `dismissNonce` is the intentional re-read trigger on dismiss click —
    // sessionStorage changes aren't something React can subscribe to.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [balance, isLow, dismissNonce]);

  if (!shouldShow || !balance) return null;

  const dollars = (balance.balanceCents / 100).toFixed(2);
  const threshold = (balance.lowBalanceThresholdCents / 100).toFixed(2);

  const onDismiss = () => {
    writeDismissedCents(balance.balanceCents);
    setDismissNonce((n) => n + 1);
  };

  return (
    <div
      role="alert"
      className={cn(
        'flex items-center gap-3 px-4 sm:px-6 py-2 border-b text-sm',
        isDark
          ? 'bg-red-500/10 border-red-500/20 text-red-200'
          : 'bg-red-50 border-red-200 text-red-800',
      )}
    >
      <AlertTriangle size={16} className="shrink-0" />
      <span className="flex-1 min-w-0">
        <span className="font-semibold">Low balance:</span>{' '}
        <span className="tabular-nums">${dollars}</span> remaining (alert threshold{' '}
        <span className="tabular-nums">${threshold}</span>). Top up to keep your agents active.
      </span>
      <button
        onClick={() => navigate('/billing')}
        className={cn(
          'shrink-0 rounded-md px-3 py-1 text-xs font-semibold transition-colors',
          isDark
            ? 'bg-red-500/20 text-red-100 hover:bg-red-500/30'
            : 'bg-red-600 text-white hover:bg-red-700',
        )}
      >
        Top up now
      </button>
      <button
        onClick={onDismiss}
        className={cn(
          'shrink-0 rounded p-1 transition-colors',
          isDark ? 'text-red-200/60 hover:text-red-100' : 'text-red-700/60 hover:text-red-900',
        )}
        aria-label="Dismiss low-balance warning"
      >
        <X size={14} />
      </button>
    </div>
  );
}
